import Dexie, { type EntityTable } from "dexie";

import type {
  CreateEmergencyLightCheckItemResponseDto,
  EmergencyLightCheckItemDto,
  EmergencyLightCheckKind,
} from "@/generated/model";

// Types for offline storage

/**
 * Cached list of scheduled questionnaires for a nursery.
 * Key: "checklists-{nurseryId}"
 */
export interface OfflineChecklist {
  id: string; // Cache key: "checklists-{nurseryId}"
  instanceId: string; // nurseryId
  title: string;
  templateName: string;
  templateDescription: string | null;
  items: unknown; // JSON-serialized ScheduledQuestionnaireListItemDto[]
  dueDate: string | null;
  cachedAt: number;
}

/**
 * Cached scheduled questionnaire detail with template and questions.
 * Key: scheduled questionnaire ID
 */
export interface OfflineQuestionnaireDetail {
  id: string; // scheduledQuestionnaireId
  data: unknown; // JSON-serialized GetScheduledQuestionnaireResponseDto
  cachedAt: number;
}

/**
 * Cached user session data for offline access.
 */
export interface OfflineUserSession {
  id: string; // "current-user"
  userId: string;
  firebaseId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  memberships: unknown; // JSON-serialized Membership[]
  nurseryAccess: unknown; // JSON-serialized NurseryAccess[]
  cachedAt: number;
}

/**
 * Cached nursery context data for offline access.
 */
export interface OfflineNurseryContext {
  id: string; // "nursery-context"
  /**
   * The user this cache belongs to. Without it the row is shared by every account
   * that signs in on this device, so a second user inherits the first user's active
   * organisation, selected nursery and cached org/nursery lists — which is how a
   * nursery manager ended up seeing the group-admin Nursery Group nav. Not indexed,
   * so it needs no store-version bump.
   */
  userId: string | null;
  selectedNurseryId: string | null;
  selectedOrgSlug: string | null;
  activeOrgId: string | null;
  organisations: unknown; // JSON-serialized OrganisationItemDto[]
  orgNurseries: unknown; // JSON-serialized NurseryItemDto[]
  cachedAt: number;
}

export interface OfflineCompletion {
  id: string; // client-generated UUID
  instanceId: string; // questionnaireId
  nurseryId: string;
  practitionerId: string;
  startedAt: string;
  completedAt: string | null;
  syncState: "pending" | "synced" | "error";
  serverId?: string; // Server-assigned ID after sync
  lastError?: string;
}

export interface OfflineAnswer {
  id: string; // client-generated UUID
  completionId: string; // references OfflineCompletion.id
  itemId: string;
  answer: "yes" | "no" | "na" | number | string;
  notes: string | null;
  syncState: "pending" | "synced" | "error";
  serverId?: string;
  lastError?: string;
}

export interface OfflinePhoto {
  id: string; // client-generated UUID
  answerId: string; // references OfflineAnswer.id
  data: ArrayBuffer; // Store as ArrayBuffer instead of Blob for iOS/Safari compatibility
  mimeType: string;
  fileName: string;
  syncState: "pending" | "synced" | "error";
  serverId?: string;
  storagePath?: string;
  lastError?: string;
}

/**
 * Cached follow-up actions for a nursery.
 * Key: "follow-up-actions-{nurseryId}"
 */
export interface OfflineFollowUpActions {
  id: string; // Cache key: "follow-up-actions-{nurseryId}"
  nurseryId: string;
  items: unknown; // FollowUpActionResponseDto[]
  cachedAt: number;
}

/**
 * Offline follow-up action completion data.
 * Key: action.id (server ID)
 */
export interface OfflineFollowUpCompletion {
  id: string; // = action.id (server ID, used as key)
  completionNotes: string;
  completedAt: string | null;
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  lastError?: string;
}

/**
 * Offline photo for a follow-up action completion.
 * Key: client-generated UUID
 */
export interface OfflineFollowUpPhoto {
  id: string; // client-generated UUID
  actionId: string; // references OfflineFollowUpCompletion.id
  data: ArrayBuffer; // iOS/Safari compatible
  mimeType: string;
  fileName: string;
  syncState: "pending" | "synced" | "error";
  serverId?: string; // fileId from server (for retry deduplication)
  lastError?: string;
}

/**
 * Offline log entry for any log type (sleep checks, temperature logs, etc).
 * Generic: works for any log type by storing the data payload as-is.
 * `coldStorageId` + `coldStorageName` + `notes` were added in NAU-219d so
 * fridge-temperature logs flow through the same queue.
 */
export interface OfflineLogEntry {
  id: string; // client-generated UUID
  nurseryId: string;
  logTypeId: string;
  roomId?: string;
  /** Set for fridge-temperature logs (and any future log type that links to a cold-storage unit). */
  coldStorageId?: string;
  /** Cached display name for the cold-storage unit at submit time — drives the panel UI without an extra fetch. */
  coldStorageName?: string;
  /** The call point a weekly fire alarm test covered (NAU-411 AC23). */
  fireAlarmCallPointId?: string;
  /** The fire door an inspection covered (NAU-441). */
  fireDoorId?: string;
  /** Free-text note (required for out-of-range fridge entries; optional otherwise). */
  notes?: string;
  data: Record<string, unknown>; // e.g. { type: "check" } or { type: "session_end" }
  timestamp: string; // ISO string, device clock at time of tap
  recordedByUserId: string;
  recordedByName: string; // cached for display
  syncState: "pending" | "synced" | "error";
  serverId?: string; // server entry ID after sync
  lastError?: string;
}

/**
 * Cache for server log entries fetched during an online session.
 * Key: "log-entries-{sessionId}"
 */
export interface OfflineLogEntriesCache {
  id: string; // "log-entries-{sessionId}"
  sessionId: string;
  entries: unknown; // LogEntryResponseDto[]
  cachedAt: number;
}

/**
 * Cache for today's log sessions.
 * Key: "log-sessions-{nurseryId}"
 */
export interface OfflineLogSessionsCache {
  id: string; // "log-sessions-{nurseryId}"
  nurseryId: string;
  sessions: unknown; // TodaySessionResponseDto[]
  cachedAt: number;
}

/**
 * Cached compliance task instance + its definition (instance->definition is a 1-1
 * lookup via taskDefinitionId; we cache both in one row keyed by instanceId so the
 * completion screen can render fully offline).
 * Key: instanceId
 */
export interface OfflineComplianceInstanceCache {
  id: string; // = instanceId
  definitionId: string;
  nurseryId: string;
  instance: unknown; // serialized ComplianceTaskInstanceResponseDto
  definition: unknown; // serialized ComplianceTaskDefinitionResponseDto
  cachedAt: number;
}

/**
 * Offline compliance task completion data — the practitioner's in-progress / pending
 * submission for a specific instance. Mirrors the OfflineFollowUpCompletion pattern.
 * Key: instanceId
 */
export interface OfflineComplianceCompletion {
  id: string; // = instanceId
  nurseryId: string;
  notes: string;
  /** Map of itemDefinitionId -> { result, notes } for CHECKLIST mode */
  checklistResults: Record<
    string,
    {
      result: "PASS" | "FAIL" | "NOT_APPLICABLE";
      notes?: string;
    }
  >;
  /** Map of field name -> value for EXTERNAL_SERVICE mode */
  externalServiceData: Record<string, unknown>;
  /**
   * Due date for the next instance in this series — typically the certificate
   * expiry. Optional: omitting it on submit ends the recurrence.
   */
  nextDueDate?: string;
  /**
   * When true, the practitioner explicitly chose to park the submission in
   * AWAITING_EVIDENCE (used for evidence-optional tasks where the cert is
   * expected to arrive later). The backend routes accordingly.
   */
  submitAsAwaiting?: boolean;
  startedAt: string;
  completedAt: string | null;
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  /** Set if the server rejected with a conflict (already completed by another user) */
  conflict?: boolean;
  lastError?: string;
}

/**
 * Offline photo / evidence file for a compliance task instance.
 * Key: client-generated UUID
 */
export interface OfflineCompliancePhoto {
  id: string; // client-generated UUID
  instanceId: string; // for per-instance gating in finalize handler
  data: ArrayBuffer; // iOS/Safari compatible
  mimeType: string;
  fileName: string;
  syncState: "pending" | "synced" | "error";
  serverId?: string; // fileId from server (for retry deduplication)
  lastError?: string;
}

/**
 * Cached open incidents for a nursery. Populated whenever the dashboard
 * widget fetches and used as the offline fallback by both the widget and
 * the FridgeTempPanel "you are in an open incident" badge.
 *
 * One row per OPEN incident. Resolved incidents are removed (sign-off
 * sync removes the row + the widget refetch confirms the absence).
 * Key: incidentId.
 */
export interface OfflineOpenIncident {
  id: string; // = incidentId
  nurseryId: string;
  coldStorageId: string;
  coldStorageName: string;
  openedAt: string; // ISO
  firstReading: {
    id: string;
    temperature: number;
    thresholdMinAtLog: number | null;
    thresholdMaxAtLog: number | null;
    recordedAt: string; // ISO
    notes: string | null;
  };
  /** Sign-off is locked until the latest reading is back in range.
   *  Optional — rows cached before the upgrade lack it; treat missing as locked. */
  canSignOff?: boolean;
  cachedAt: number;
}

/**
 * Pending sign-off submission queued for the next online sync.
 * The dialog enqueues this + immediately drops the matching row from
 * `openIncidentsCache` so the dashboard rail updates without a round-trip.
 * Key: incidentId (deduplicates accidental double-submits).
 */
export interface OfflineIncidentSignoff {
  id: string; // = incidentId
  resolutionNotes: string | null;
  queuedAt: string; // ISO
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  lastError?: string;
}

/**
 * Cached open (unacknowledged) hot-holding alert for the manager dashboard's
 * "open food alerts" rail (NAU-220). The food sibling of OfflineOpenIncident —
 * but the model is a flat single reading (no incident grouping, no detail page).
 * Key: alertId.
 */
export interface OfflineOpenHotHoldingAlert {
  id: string; // = alertId
  nurseryId: string;
  logEntryId: string;
  mealId: string;
  mealName: string;
  temperature: number;
  thresholdAtLog: number;
  recordedAt: string; // ISO
  notes: string | null;
  cachedAt: number;
}

/**
 * Pending acknowledge submission queued for the next online sync (NAU-220).
 * The dashboard enqueues this + immediately drops the matching row from
 * `openHotHoldingAlerts` so the rail updates without a round-trip.
 * Key: alertId (deduplicates accidental double-taps). The food sibling of
 * OfflineIncidentSignoff, minus resolution notes (acknowledge is a bare action).
 */
/**
 * A whole emergency lighting check captured offline, queued as one item so the
 * server's AC13 completeness rule is met or not atomically on replay.
 */
export interface OfflineEmergencyLightCheck {
  id: string; // local uuid until the server assigns one
  nurseryId: string;
  kind: EmergencyLightCheckKind;
  items: EmergencyLightCheckItemDto[];
  queuedAt: string; // ISO
  /**
   * Who recorded this on the device; sent on replay so the server credits them,
   * not whoever is signed in when the queue drains (NAU-399).
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  lastError?: string;
  /** Server ids written back on replay; "Submit and Raise Follow-up" needs the failed item's. */
  serverId?: string;
  serverItems?: CreateEmergencyLightCheckItemResponseDto[];
}

/**
 * A nursery's register of units to check (emergency lights, fire alarm call
 * points), cached so the check forms still list them on an offline cold start.
 * Key: "{register}-{nurseryId}", e.g. "emergency-lights-{nurseryId}"
 */
export interface OfflineRegisterCache {
  id: string;
  nurseryId: string;
  items: unknown; // the register's list response rows
  cachedAt: number;
}

export interface OfflineHotHoldingAlertAck {
  id: string; // = alertId
  queuedAt: string; // ISO
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  lastError?: string;
}

/**
 * A cached list of the current user's outstanding acknowledgement requests.
 *
 * Keyed by `my-acknowledgements-{nurseryId}-{userId}`, NOT by nursery alone.
 * The user id is load-bearing: shared-device PIN login (NAU-226) means two
 * people use one tablet, and a nursery-keyed row would show one staff member's
 * outstanding legal acknowledgements to whoever signed in next. Same reasoning
 * as OfflineNurseryContext.userId, higher stakes.
 */
export interface OfflineAcknowledgementRequests {
  id: string;
  nurseryId: string;
  userId: string;
  cachedAt: string;
  /** The list payload, cached whole. */
  requests: unknown;
}

/** One request's document, cached so it can be read offline (AC11 + AC35). */
export interface OfflineAcknowledgementRequestDetail {
  id: string; // = requestId
  cachedAt: string;
  detail: unknown;
}

/**
 * One person's offline read and/or signature for one request.
 *
 * ONE row with two nullable timestamps and two sync states, rather than two
 * tables: Read and Signed are two facts about the same request, and splitting
 * them would need cross-table reconciliation on replay and would double the
 * surface `clearAllOfflineData` has to remember.
 */
export interface OfflineAcknowledgementAck {
  id: string; // = requestId
  queuedAt: string; // ISO
  readAt?: string;
  signedAt?: string;
  readSyncState?: "pending" | "synced" | "error";
  signSyncState?: "pending" | "synced" | "error";
  lastError?: string;
  /**
   * The To do row that signing optimistically removed, kept so the sync worker
   * can put it back if the signature never lands. Without it an exhausted
   * signature vanishes silently — the row is gone from the list, so the
   * "Sync failed" badge has nothing to render against.
   *
   * Not indexed, so this needs no `.version()` bump — Dexie stores whole
   * objects and only declared indexes are versioned.
   */
  removedListEntry?: unknown;
  removedListKey?: string;
}

/**
 * Offline event record submission. One-off structured event records
 * (fire drill, incident, parent complaint) submitted by a leader+ while the
 * device may be offline. Mirrors the OfflineLogEntry create-and-sync pattern:
 * the record is immutable, so there is no offline edit/acknowledge — only
 * create. `eventTypeName` is cached at submit time so a pending row can render
 * without an extra fetch.
 */
export interface OfflineEventRecord {
  id: string; // client-generated UUID
  nurseryId: string;
  eventTypeId: string;
  eventTypeName: string; // cached for display of the pending row
  data: Record<string, unknown>; // keyed by the event type field schema keys
  timestamp: string; // ISO string, device clock at time of submit
  recordedByUserId: string;
  recordedByName: string; // cached for display
  syncState: "pending" | "synced" | "error";
  serverId?: string; // server record ID after sync
  lastError?: string;
  /**
   * People to ask to acknowledge this record (NAU-342). Captured at submit time
   * and replayed on sync. Not indexed, so no store-version bump is needed.
   */
  acknowledgementRequestUserIds?: string[];
}

/**
 * Offline hot holding meal (NAU-93). A meal + its first (delivery) reading are
 * created together by one endpoint, so "log a delivery" writes one meal row +
 * one reading row (isFirstReading) and enqueues a single `hotHoldingMeal` sync
 * item. On sync the meal POST returns { id, readingId }, which back-fill the
 * serverIds and unblock any queued service readings (parent→child, mirrors the
 * answer→photo pattern).
 */
export interface OfflineHotHoldingMeal {
  id: string; // client-generated local UUID
  nurseryId: string;
  mealName: string;
  timestamp: string; // ISO, device clock at create
  recordedByName: string; // cached for display
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  serverId?: string; // server meal id after sync
  lastError?: string;
}

export interface OfflineHotHoldingReading {
  id: string; // client-generated local UUID
  nurseryId: string;
  /** Parent meal when it is still local-pending (its serverId isn't known yet). */
  mealLocalId?: string;
  /** Parent meal when it is already synced (a reading added to a server meal). */
  mealServerId?: string;
  readingType: "DELIVERY" | "SERVICE";
  temperature: number;
  notes?: string;
  /** The meal's first reading — created together with the meal, synced by the
   *  meal's POST rather than its own queue item. */
  isFirstReading: boolean;
  timestamp: string; // ISO
  recordedByName: string; // cached for display
  /**
   * The practitioner who performed this on the device. Sent on replay so the
   * server attributes it to them rather than to whoever happens to be signed in
   * when the queue drains (NAU-399). Optional: rows queued before this existed
   * have none, and fall back to the sender exactly as they did before.
   */
  capturedByUserId?: string;
  syncState: "pending" | "synced" | "error";
  serverId?: string; // server reading (log entry) id after sync
  lastError?: string;
}

export interface SyncQueueItem {
  id: string;
  type:
    | "completion"
    | "answer"
    | "photo"
    | "finalize"
    | "followUpPhoto"
    | "followUpFinalize"
    | "logEntry"
    | "compliancePhoto"
    | "complianceFinalize"
    | "incidentSignoff"
    | "eventRecord"
    | "hotHoldingMeal"
    | "hotHoldingReading"
    | "hotHoldingAlertAck"
    | "acknowledgementSign"
    | "acknowledgementRead"
    | "emergencyLightCheck";
  entityId: string;
  priority: number; // Lower = higher priority
  createdAt: number;
  attempts: number;
  lastAttempt?: number;
  lastError?: string;
  /**
   * Set when the server refused this item for a reason a DIFFERENT session
   * could fix (401/403/404 — e.g. a manager's acknowledgement queued on a
   * shared device that only a manager, or the delegation gate, can land).
   * Parked items are skipped, without burning retry attempts, until the
   * signed-in user changes (NAU-399).
   */
  deferredUntilAuthChange?: boolean;
  /**
   * How many times this item has been parked. Bounded by MAX_PARK_CYCLES so an
   * item no session can ever land (a record deleted server-side 404s forever)
   * eventually falls through to normal retry semantics, becomes visible as
   * failed, and stops blocking device reset.
   */
  parkCycles?: number;
  /**
   * The Firebase uid this item was parked against. Un-parking keys off this
   * rather than an in-memory "last uid", which reset on every page load and so
   * un-parked items for the *same* signed-in user — spending their bounded park
   * cycles on a session already proven unable to land them.
   */
  parkedForUid?: string;
}

// Create the database
/**
 * Device→nursery link for shared-device PIN login (NAU-226).
 *
 * Deliberately a single-row store keyed on a fixed id. Unlike every other table
 * here, it is EXCLUDED from `clearAllOfflineData()` so the link survives logout
 * / auto-logout (Decision 5) — only an explicit "reset device" clears it.
 */
export interface OfflineDeviceLink {
  id: string; // fixed key: DEVICE_LINK_KEY
  deviceUuid: string;
  /**
   * Credential issued when the device was linked, sent as `x-device-secret` on
   * the calls that happen before anyone signs in. Optional only because links
   * saved before it existed have none — those devices must be set up again.
   */
  deviceSecret?: string;
  nurseryId: string;
  nurseryName: string;
  pinLoginEnabled: boolean;
  cachedUsers: unknown; // JSON-serialized LinkedUserDto[]
  cachedAt: number;
}

class OfflineDatabase extends Dexie {
  checklists!: EntityTable<OfflineChecklist, "id">;
  questionnaireDetails!: EntityTable<OfflineQuestionnaireDetail, "id">;
  userSession!: EntityTable<OfflineUserSession, "id">;
  nurseryContext!: EntityTable<OfflineNurseryContext, "id">;
  completions!: EntityTable<OfflineCompletion, "id">;
  answers!: EntityTable<OfflineAnswer, "id">;
  photos!: EntityTable<OfflinePhoto, "id">;
  followUpActions!: EntityTable<OfflineFollowUpActions, "id">;
  followUpCompletions!: EntityTable<OfflineFollowUpCompletion, "id">;
  followUpPhotos!: EntityTable<OfflineFollowUpPhoto, "id">;
  logEntries!: EntityTable<OfflineLogEntry, "id">;
  logEntriesCache!: EntityTable<OfflineLogEntriesCache, "id">;
  logSessionsCache!: EntityTable<OfflineLogSessionsCache, "id">;
  complianceInstances!: EntityTable<OfflineComplianceInstanceCache, "id">;
  complianceCompletions!: EntityTable<OfflineComplianceCompletion, "id">;
  compliancePhotos!: EntityTable<OfflineCompliancePhoto, "id">;
  openIncidents!: EntityTable<OfflineOpenIncident, "id">;
  incidentSignoffs!: EntityTable<OfflineIncidentSignoff, "id">;
  openHotHoldingAlerts!: EntityTable<OfflineOpenHotHoldingAlert, "id">;
  hotHoldingAlertAcks!: EntityTable<OfflineHotHoldingAlertAck, "id">;
  acknowledgementRequests!: EntityTable<OfflineAcknowledgementRequests, "id">;
  acknowledgementRequestDetails!: EntityTable<
    OfflineAcknowledgementRequestDetail,
    "id"
  >;
  acknowledgementAcks!: EntityTable<OfflineAcknowledgementAck, "id">;
  emergencyLightChecks!: EntityTable<OfflineEmergencyLightCheck, "id">;
  registerCache!: EntityTable<OfflineRegisterCache, "id">;
  eventRecords!: EntityTable<OfflineEventRecord, "id">;
  hotHoldingMeals!: EntityTable<OfflineHotHoldingMeal, "id">;
  hotHoldingReadings!: EntityTable<OfflineHotHoldingReading, "id">;
  syncQueue!: EntityTable<SyncQueueItem, "id">;
  deviceLink!: EntityTable<OfflineDeviceLink, "id">;

  constructor() {
    super("NurseryChecklistsDB");

    this.version(1).stores({
      checklists: "id, instanceId, cachedAt",
      completions: "id, instanceId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 2: Add nurseryId to completions
    this.version(2).stores({
      checklists: "id, instanceId, cachedAt",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 3: Add tables for offline-first caching (questionnaire details, user session, nursery context)
    this.version(3).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 4: Add follow-up actions cache
    this.version(4).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 5: Add follow-up action completion offline support
    this.version(5).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 6: Add generic offline log entries + caches (sleep checks, etc.)
    this.version(6).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 7: Add compliance task instance offline support (NAU-16 phase 4)
    this.version(7).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 8: Add out-of-range incident offline support (NAU-219d)
    this.version(8).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 9: Add offline event record submission
    this.version(9).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 10: Add hot holding meal + reading offline support (NAU-93)
    this.version(10).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      hotHoldingMeals: "id, nurseryId, syncState, timestamp",
      hotHoldingReadings: "id, mealLocalId, mealServerId, syncState, timestamp",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 11: Add hot holding alert offline support (NAU-220)
    this.version(11).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      openHotHoldingAlerts: "id, nurseryId, mealId, cachedAt",
      hotHoldingAlertAcks: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      hotHoldingMeals: "id, nurseryId, syncState, timestamp",
      hotHoldingReadings: "id, mealLocalId, mealServerId, syncState, timestamp",
      syncQueue: "id, type, entityId, priority, createdAt",
    });

    // Version 12: Add shared-device → nursery link for PIN login (NAU-226)
    this.version(12).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      openHotHoldingAlerts: "id, nurseryId, mealId, cachedAt",
      hotHoldingAlertAcks: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      hotHoldingMeals: "id, nurseryId, syncState, timestamp",
      hotHoldingReadings: "id, mealLocalId, mealServerId, syncState, timestamp",
      syncQueue: "id, type, entityId, priority, createdAt",
      deviceLink: "id",
    });

    // NAU-410: the shared acknowledgement system's three tables.
    this.version(13).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      openHotHoldingAlerts: "id, nurseryId, mealId, cachedAt",
      hotHoldingAlertAcks: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      hotHoldingMeals: "id, nurseryId, syncState, timestamp",
      hotHoldingReadings: "id, mealLocalId, mealServerId, syncState, timestamp",
      acknowledgementRequests: "id, nurseryId, userId, cachedAt",
      acknowledgementRequestDetails: "id, cachedAt",
      acknowledgementAcks: "id, signSyncState, readSyncState",
      syncQueue: "id, type, entityId, priority, createdAt",
      deviceLink: "id",
    });

    // v14 (NAU-411): emergency lighting checks captured offline. Additive — a
    // new store only, so Dexie upgrades in place and existing queues survive.
    this.version(14).stores({
      emergencyLightChecks: "id, nurseryId, kind, syncState, queuedAt",
    });

    // v15 (NAU-435): the emergency light and call point registers, cached so a
    // check can be recorded on an offline cold start.
    this.version(15).stores({
      checklists: "id, instanceId, cachedAt",
      questionnaireDetails: "id, cachedAt",
      userSession: "id",
      nurseryContext: "id",
      followUpActions: "id, nurseryId, cachedAt",
      followUpCompletions: "id, syncState",
      followUpPhotos: "id, actionId, syncState",
      completions: "id, instanceId, nurseryId, practitionerId, syncState",
      answers: "id, completionId, itemId, syncState",
      photos: "id, answerId, syncState",
      logEntries: "id, nurseryId, logTypeId, roomId, syncState, timestamp",
      logEntriesCache: "id, sessionId, cachedAt",
      logSessionsCache: "id, nurseryId, cachedAt",
      complianceInstances: "id, definitionId, nurseryId, cachedAt",
      complianceCompletions: "id, nurseryId, syncState",
      compliancePhotos: "id, instanceId, syncState",
      openIncidents: "id, nurseryId, coldStorageId, cachedAt",
      incidentSignoffs: "id, syncState",
      openHotHoldingAlerts: "id, nurseryId, mealId, cachedAt",
      hotHoldingAlertAcks: "id, syncState",
      eventRecords: "id, nurseryId, eventTypeId, syncState, timestamp",
      hotHoldingMeals: "id, nurseryId, syncState, timestamp",
      hotHoldingReadings: "id, mealLocalId, mealServerId, syncState, timestamp",
      acknowledgementRequests: "id, nurseryId, userId, cachedAt",
      acknowledgementRequestDetails: "id, cachedAt",
      acknowledgementAcks: "id, signSyncState, readSyncState",
      emergencyLightChecks: "id, nurseryId, kind, syncState, queuedAt",
      registerCache: "id, nurseryId, cachedAt",
      syncQueue: "id, type, entityId, priority, createdAt",
      deviceLink: "id",
    });

    // Cross-tab safety: if another tab loads a newer schema and bumps the version,
    // close this connection and reload so we never read stale typings against newer
    // tables (or vice versa). Cheap mitigation; the alternative is a hard error.
    this.on("versionchange", () => {
      this.close();
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    });
  }
}

export const offlineDb = new OfflineDatabase();

/**
 * The signed-in user's id, from the cached session.
 *
 * Stamped onto offline records at capture time so a replay can tell the server
 * who actually did the work, rather than letting it default to whoever is
 * signed in when the queue finally drains (NAU-399).
 */
export async function cachedUserId(): Promise<string | undefined> {
  const session = await offlineDb.userSession.get("current-user");
  return session?.userId;
}

/**
 * Assert we know who is capturing this record.
 *
 * Falling back to "" here is worse than failing: the sync layer omits a falsy
 * author, so the server attributes the record to whoever drains the queue —
 * silently reintroducing the mis-attribution NAU-399 exists to prevent, with
 * no error anyone would see. The cached session is written at login and only
 * cleared at logout, so an absent one means something is genuinely wrong.
 */
export function requireCachedUserId(userId: string | undefined): string {
  if (!userId) {
    throw new Error(
      "Cannot record this offline: no signed-in user is cached on this device. Sign in again and retry.",
    );
  }

  return userId;
}

// Helper to generate UUIDs with fallback for non-secure contexts
export function generateClientId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers or non-HTTPS contexts
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Service worker cache names that may contain user-specific data
const USER_DATA_CACHE_NAMES = ["pages-cache", "next-rsc-cache", "api-cache"];

/**
 * Read-model tables. Everything here is a copy of something the server already
 * holds, fetched for offline reading, so clearing it costs nothing but a
 * refetch — and `userSession` / `nurseryContext` carry the signed-in user's
 * name, email, roles and memberships, which must not outlive their session on
 * a shared device.
 */
const CACHE_TABLES = () => [
  offlineDb.checklists,
  offlineDb.questionnaireDetails,
  offlineDb.userSession,
  offlineDb.nurseryContext,
  offlineDb.followUpActions,
  offlineDb.logEntriesCache,
  offlineDb.logSessionsCache,
  offlineDb.complianceInstances,
  offlineDb.openIncidents,
  offlineDb.openHotHoldingAlerts,
  // NAU-410: both are server copies — the outstanding list and the cached
  // documents behind it. Per-user, so they must not outlive the session.
  offlineDb.acknowledgementRequests,
  offlineDb.acknowledgementRequestDetails,
  offlineDb.registerCache,
];

/**
 * Tables holding work captured ON this device, plus the queue that ships it.
 * A row here may be the ONLY copy of something a practitioner recorded, so
 * these are never cleared by a logout (NAU-399).
 */
/**
 * Names of the work tables, for callers that need to iterate them generically
 * (currently the debug sync-status screen). Kept beside WORK_TABLES so a new
 * table can't be added to one and forgotten in the other.
 */
export const WORK_TABLE_NAMES = [
  "completions",
  "answers",
  "photos",
  "followUpCompletions",
  "followUpPhotos",
  "logEntries",
  "complianceCompletions",
  "compliancePhotos",
  "incidentSignoffs",
  "hotHoldingAlertAcks",
  "eventRecords",
  "hotHoldingMeals",
  "hotHoldingReadings",
  "acknowledgementAcks",
  "emergencyLightChecks",
] as const;

const WORK_TABLES = () => [
  offlineDb.completions,
  offlineDb.answers,
  offlineDb.photos,
  offlineDb.followUpCompletions,
  offlineDb.followUpPhotos,
  offlineDb.logEntries,
  offlineDb.complianceCompletions,
  offlineDb.compliancePhotos,
  offlineDb.incidentSignoffs,
  offlineDb.hotHoldingAlertAcks,
  offlineDb.eventRecords,
  offlineDb.hotHoldingMeals,
  offlineDb.hotHoldingReadings,
  // NAU-410: a signature made offline. Until it reaches the server this row
  // is the only record that the staff member signed at all, and AC17 says it
  // can never be recreated — so a logout must not take it.
  offlineDb.acknowledgementAcks,
  offlineDb.emergencyLightChecks,
  offlineDb.syncQueue,
];

/**
 * Clear the offline caches, by default keeping anything not yet on the server.
 *
 * Clearing every table unconditionally deletes the `syncQueue` and the rows it
 * points at, so a practitioner who recorded checks with no signal and then
 * signed out — or was signed out by the inactivity timer — silently loses that
 * work: never sent, and no longer on the device.
 *
 * Logout can't refuse the way a device reset can — the user is leaving, and the idle
 * timer fires with nobody there to answer a prompt — so it preserves instead.
 * The queue survives, and the sync service drains it on the next session under
 * whoever signs in next.
 *
 * WORK_TABLES are preserved WHOLESALE rather than filtered down to
 * `syncState === "pending"` rows. Queue items reach through parents to
 * children — a `finalize` resolves its completion's answers, a hot-holding
 * reading resolves its meal — so deleting a synced parent can strand a pending
 * child with no way to sync. Nothing reaps these rows today: a snapshot-based
 * sweep cannot see that an open screen is about to attach a child to a row it
 * judged finished, which is how a live questionnaire completion was deleted
 * mid-flow. Reaping needs a design that accounts for open screens.
 * The retained rows are nursery records (checks, readings, log entries), not
 * the departing user's account data — that lives in the cache tables above.
 *
 * NAU-226 Decision 5: the `deviceLink` table is INTENTIONALLY excluded from
 * both lists so a shared device stays linked to its nursery across logout /
 * auto-logout — only an explicit "reset device" clears it (see clearDeviceLink
 * in lib/device/device-link). Do not add `deviceLink` to either list.
 *
 * @param options.keepUnsyncedWork Defaults to true. Pass false ONLY for an
 *   explicit, user-initiated wipe (the non-production "Clear Offline Data"
 *   debug action) — never on a logout path.
 */
export async function clearAllOfflineData(
  options: { keepUnsyncedWork?: boolean } = {},
): Promise<void> {
  const { keepUnsyncedWork = true } = options;

  const tables = keepUnsyncedWork
    ? CACHE_TABLES()
    : [...CACHE_TABLES(), ...WORK_TABLES()];

  await offlineDb.transaction("rw", tables, async () => {
    for (const table of tables) {
      await table.clear();
    }
  });

  // Clear service worker caches that may contain user-specific data
  if ("caches" in window) {
    try {
      for (const cacheName of USER_DATA_CACHE_NAMES) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        await Promise.all(keys.map((key) => cache.delete(key)));
      }
    } catch (err) {
      console.error("Failed to clear service worker caches:", err);
    }
  }
}

// Merge a patch into a row of the nurseryContext table inside a transaction.
// Several callers (nursery list cache, selected-nursery cache, organisations
// cache) write to this table concurrently during login; a non-transactional
// get-then-put lets them clobber each other's fields (NAU-335).
/**
 * Discards the cached nursery/organisation context when it belongs to a different
 * user, and stamps it for the current one.
 *
 * Both cache rows (`"organisations"` and `"nursery-context"`) are keyed by a fixed
 * string rather than by user, and nothing used to clear them, so signing in as a
 * second account on the same device inherited the first account's active
 * organisation and selected nursery. That is how a nursery manager — who holds no
 * organisation membership at all — reached the group-admin Nursery Group screens.
 *
 * Called on sign-in rather than only on sign-out, so it also covers the case where
 * a session simply expires and someone else signs in without logging out.
 */
export async function claimNurseryContextForUser(
  userId: string,
): Promise<void> {
  await offlineDb.transaction("rw", offlineDb.nurseryContext, async () => {
    const rows = await offlineDb.nurseryContext.toArray();
    for (const row of rows) {
      if (row.userId === userId) {
        continue;
      }
      await offlineDb.nurseryContext.put({
        ...row,
        userId,
        selectedNurseryId: null,
        selectedOrgSlug: null,
        activeOrgId: null,
        organisations: [],
        orgNurseries: [],
        cachedAt: Date.now(),
      });
    }
  });
}

/** Wipes the cached nursery/organisation context. Used on sign-out. */
export async function clearNurseryContextCache(): Promise<void> {
  await offlineDb.nurseryContext.clear();
}

export async function patchNurseryContextRow(
  id: string,
  patch: Partial<Omit<OfflineNurseryContext, "id" | "cachedAt">>,
): Promise<void> {
  await offlineDb.transaction("rw", offlineDb.nurseryContext, async () => {
    const existing = await offlineDb.nurseryContext.get(id);
    await offlineDb.nurseryContext.put({
      id,
      userId: existing?.userId ?? null,
      selectedNurseryId: existing?.selectedNurseryId ?? null,
      selectedOrgSlug: existing?.selectedOrgSlug ?? null,
      activeOrgId: existing?.activeOrgId ?? null,
      organisations: existing?.organisations ?? [],
      orgNurseries: existing?.orgNurseries ?? [],
      ...patch,
      cachedAt: Date.now(),
    });
  });
}
